import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Award, Shield, CheckCircle, ExternalLink } from "lucide-react";
import { cqcRating } from "../mock";

export const CQCRating = () => {
  return (
    <section id="cqc" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200">
            Regulated & Inspected
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Care Quality Commission Rating
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're proud of our CQC rating and our commitment to continuous improvement
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Main CQC Badge */}
          <div className="mb-12">
            <Card className="border-4 border-teal-600 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full">
                    <Award className="h-16 w-16" />
                  </div>
                </div>
                <h3 className="text-3xl font-bold mb-2">Overall Rating</h3>
                <div className="text-6xl font-bold text-orange-400 mb-2">{cqcRating.overall}</div>
                <p className="text-teal-100">Care Quality Commission Assessment</p>
                <p className="text-sm text-teal-200 mt-2">Last inspected: {cqcRating.lastInspection}</p>
              </div>
            </Card>
          </div>

          {/* Rating Categories */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
            {cqcRating.ratings.map((rating, index) => (
              <Card
                key={index}
                className="border-2 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-lg"
              >
                <CardContent className="pt-6 text-center">
                  <CheckCircle className="h-8 w-8 text-teal-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 mb-2">{rating.category}</h4>
                  <Badge className="bg-teal-600 text-white hover:bg-teal-700">
                    {rating.rating}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Explanation Section */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-none shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 text-orange-600 p-3 rounded-lg flex-shrink-0">
                    <Shield className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Fully Regulated</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Barnston Court is fully regulated by the Care Quality Commission (CQC), 
                      the independent regulator of health and social care in England.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 text-teal-600 p-3 rounded-lg flex-shrink-0">
                    <Award className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Regular Inspections</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We undergo regular unannounced inspections to ensure we maintain the 
                      highest standards of care, safety, and effectiveness.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* What CQC Assesses */}
          <Card className="bg-gradient-to-br from-teal-50 to-orange-50 border-none shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                What the CQC Assesses
              </h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-bold text-teal-700 mb-2">Safe</h4>
                  <p className="text-gray-600 text-sm">
                    Protection from abuse and avoidable harm
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-teal-700 mb-2">Effective</h4>
                  <p className="text-gray-600 text-sm">
                    Care, treatment and support achieves good outcomes
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-teal-700 mb-2">Caring</h4>
                  <p className="text-gray-600 text-sm">
                    Staff involve and treat residents with compassion
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-teal-700 mb-2">Responsive</h4>
                  <p className="text-gray-600 text-sm">
                    Services meet individual needs and preferences
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-teal-700 mb-2">Well-Led</h4>
                  <p className="text-gray-600 text-sm">
                    Leadership promotes person-centred care
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-teal-700 mb-2">Transparent</h4>
                  <p className="text-gray-600 text-sm">
                    Open communication and continuous improvement
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Link */}
          <div className="text-center mt-8">
            <a
              href="https://www.cqc.org.uk/location/1-111147708"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors"
            >
              View Our Full CQC Report
              <ExternalLink className="h-4 w-4" />
            </a>
            <p className="text-sm text-gray-500 mt-2">
              CQC Registration: {cqcRating.registrationNumber}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Note: This is a placeholder link. Please update with actual Barnston Court CQC report URL.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
